/**
 * Parte persona del F22 AT 2026 del empresario individual: las líneas que se alimentan de
 * las tarjetas validadas del panel (data.persona). Común a todos los regímenes del AT 2026:
 * cada módulo de régimen (EmpresarioIndividual/2026/<régimen>) la usa sin copiarla.
 *
 * Reglas según las instrucciones oficiales del F22 AT 2026 del SII, línea por línea:
 * - Línea 1: retiros de la DJ 1948. Las filas cuyo declarante es el mismo titular van en
 *   «a) Propios» (2005–2009) y las de otras empresas en «b) Por participaciones»
 *   (2000–2004). Los créditos se clasifican según restitución y devolución, como indica el
 *   ejemplo oficial de la línea 1. La Sección C (retiros en exceso) es informativa.
 * - Línea 8: intereses positivos de la DJ 1890 (1867 → 155). Línea 17: los negativos (169),
 *   con tope en las rentas de las líneas 8 y 9 más su incremento.
 * - Línea 9: retiros exentos del IGC de la DJ 1948 (1883) y sus créditos (1879–1882).
 * - Línea 13: sueldos de la DJ 1887, incluido el sueldo empresarial (1098 → 161).
 * - Línea 14: incremento por IDPC (159) y por IPE (748).
 * - Líneas 19 y 24: subtotal (158) y base (170). Las rebajas de las líneas 20 a 23 quedan
 *   en 0 mientras sus tarjetas estén en construcción.
 * - Línea 25: IGC según la tabla del AT 2026 (157).
 * - Línea 29: restitución del 35 % de los créditos sujetos a restitución (1035); en los
 *   sin devolución, solo de lo que alcanza a imputarse.
 * - Créditos: 136 (proporcional por rentas exentas, línea 34), 176 (tasa adicional),
 *   608 (IDPC sin devolución), 746 (IPE) y 895 (educación) no dan devolución; 162 (impuesto
 *   único) y 610 (IDPC con devolución) sí.
 * - Línea 52: 304 = obligaciones de las líneas 25 a 30 menos los créditos de las líneas
 *   31 a 51. Si es negativo, solo el sobrante de los créditos con devolución pasa a la
 *   línea 97 (119 desde el 162 y 116 desde el 610). Línea 53: 31 = 304 si es positivo.
 * - La mayor retención de sueldos (54) se informa para la liquidación (línea 92).
 */
const P = require('./parametros');

const n = (valor) => Number(valor) || 0;
const sumar = (filas, fn) => filas.reduce((total, fila) => total + n(fn(fila)), 0);
const filas = (d, tarjeta) => (d.persona && Array.isArray(d.persona[tarjeta]) ? d.persona[tarjeta] : []);

// ───── DJ 1948: clasificación de cada fila de la Sección B ─────
const retirosB = (d) => filas(d, 'retiros').filter((f) => f.seccion === 'B');
const esPropio = (f) => Boolean(f.rut_declarante) && f.rut_declarante === f.rut_receptor;

const afecto = (f) => n(f.afecto_credito_idpc_desde_2017) + n(f.afecto_credito_idpc_hasta_2016)
  + n(f.afecto_credito_idpc_voluntario) + n(f.afecto_sin_credito);
const creditoConRestitucionSinDev = (f) => n(f.credito_2017_sujeto_restitucion_sin_devolucion);
const creditoConRestitucionConDev = (f) => n(f.credito_2017_sujeto_restitucion_con_devolucion);
const creditoSinRestitucionSinDev = (f) => n(f.credito_2017_no_sujeto_hasta_2019_sin_devolucion)
  + n(f.credito_2017_no_sujeto_desde_2020_sin_devolucion) + n(f.credito_2016_afectas_sin_devolucion);
const creditoSinRestitucionConDev = (f) => n(f.credito_2017_no_sujeto_hasta_2019_con_devolucion)
  + n(f.credito_2017_no_sujeto_desde_2020_con_devolucion) + n(f.credito_2016_afectas_con_devolucion);

// ───── Línea 1 ─────
const linea1 = (d) => {
  const propios = retirosB(d).filter(esPropio);
  const terceros = retirosB(d).filter((f) => !esPropio(f));
  const c = {
    2005: sumar(propios, creditoConRestitucionSinDev),
    2006: sumar(propios, creditoConRestitucionConDev),
    2007: sumar(propios, creditoSinRestitucionSinDev),
    2008: sumar(propios, creditoSinRestitucionConDev),
    2009: sumar(propios, afecto),
    2000: sumar(terceros, creditoConRestitucionSinDev),
    2001: sumar(terceros, creditoConRestitucionConDev),
    2002: sumar(terceros, creditoSinRestitucionSinDev),
    2003: sumar(terceros, creditoSinRestitucionConDev),
    2004: sumar(terceros, afecto),
  };
  return {
    ...c,
    1592: c[2005] + c[2000],
    1024: c[2006] + c[2001],
    1593: c[2007] + c[2002],
    1025: c[2008] + c[2003],
    104: c[2009] + c[2004],
  };
};

// ───── Línea 8 y línea 17 (DJ 1890) ─────
const interesesPositivos = (d) => sumar(filas(d, 'intereses'),
  (f) => n(f.depositos_positivo) + n(f.cuentas_corrientes_positivo) + n(f.captacion_positivo));
const interesesNegativos = (d) => sumar(filas(d, 'intereses'),
  (f) => n(f.depositos_negativo) + n(f.cuentas_corrientes_negativo) + n(f.captacion_negativo));

// ───── Línea 9 (rentas exentas de la DJ 1948) ─────
const linea9 = (d) => {
  const b = retirosB(d);
  const c = {
    1879: sumar(b, (f) => f.credito_2017_exentas_sujeto_restitucion_sin_devolucion),
    1880: sumar(b, (f) => f.credito_2017_exentas_sujeto_restitucion_con_devolucion),
    1881: sumar(b, (f) => f.credito_2016_exentas_sin_devolucion),
    1882: sumar(b, (f) => f.credito_2016_exentas_con_devolucion),
    1883: sumar(b, (f) => n(f.rex_exentas_igc_afectas_ia) + n(f.rex_exentas_igc_ia)),
  };
  return { ...c, 1633: c[1879], 1105: c[1880], 1634: c[1881], 606: c[1882], 152: c[1883] };
};

// ───── IGC según tabla (línea 25) ─────
const igcSegunTabla = (base) => {
  if (base <= P.BASE_EXENTA_IGC) return 0;
  const tramo = P.TABLA_IGC.find((t) => base <= t.hasta);
  return Math.round(base * tramo.factor - tramo.rebaja);
};

// ───── Crédito por gastos en educación (línea 41) ─────
const creditoEducacion = (d, rentasLineas1a14) => {
  if (rentasLineas1a14 > P.TOPE_RENTAS_EDUCACION_UF * P.UF_31_DICIEMBRE) return 0;
  return sumar(filas(d, 'educacion').filter((h) => String(h.cumple_requisitos || '').trim().toUpperCase() !== 'N'),
    (h) => Math.round(P.UF_POR_HIJO_EDUCACION * P.UF_31_DICIEMBRE * (n(h.porcentaje_credito) / 100)));
};

/**
 * Todos los códigos de la parte persona.
 * @param {Object} d - data del formulario, con d.persona = { sueldos, intereses, retiros, educacion }
 * @returns {Object} códigos del F22 (clave = número de código)
 */
const calcularPersona = (d) => {
  const l1 = linea1(d);
  const l9 = linea9(d);
  const c1867 = interesesPositivos(d);
  const c155 = c1867;
  const c1098 = sumar(filas(d, 'sueldos'), (f) => f.renta_neta);
  const c161 = c1098;

  const incrementoLinea9 = l9[1633] + l9[1105] + l9[1634] + l9[606];
  const c159 = l1[1592] + l1[1024] + l1[1593] + l1[1025] + incrementoLinea9;
  const c748 = sumar(retirosB(d), (f) => n(f.credito_2017_ipe) + n(f.credito_2016_ipe));
  const c749 = c159 + c748;

  // Pérdida de capitales mobiliarios, con tope en las rentas de las líneas 8 y 9 y su incremento
  const c169 = Math.min(interesesNegativos(d), c155 + l9[152] + incrementoLinea9);

  const c158 = l1[104] + c155 + l9[152] + c161 + c749 - c169;
  const c170 = Math.max(c158, 0);
  const c157 = igcSegunTabla(c170);

  // Créditos que no dan devolución
  const factorExentas = c170 > 0 ? Math.round((c157 / c170) * 1000) / 1000 : 0;
  const c136 = Math.round(factorExentas * (l9[152] + incrementoLinea9));
  const c176 = sumar(retirosB(d), (f) => f.credito_tasa_adicional);
  const c608 = l1[1592] + l1[1593] + l9[1633] + l9[1634];
  const c746 = c748;
  const c895 = creditoEducacion(d, l1[104] + c155 + l9[152] + c161 + c749);

  // Créditos que dan devolución
  const c162 = sumar(filas(d, 'sueldos'), (f) => f.impuesto_unico_retenido);
  const c610 = l1[1024] + l1[1025] + l9[1105] + l9[606];

  // Restitución (línea 29): sobre lo con devolución completo; sobre lo sin devolución, lo imputable
  const sujetosSinDev = l1[1592] + l9[1633];
  const sujetosConDev = l1[1024] + l9[1105];
  const c1035 = Math.round(P.TASA_RESTITUCION * (Math.min(sujetosSinDev, c157) + sujetosConDev));

  const obligaciones = c157 + c1035;
  const creditos = c136 + c176 + c608 + c746 + c895 + c162 + c610;
  const c304 = obligaciones - creditos;
  const c31 = Math.max(c304, 0);

  // Remanente devolvible (línea 97): solo el sobrante de los créditos con devolución
  const remanente = c304 < 0 ? Math.min(-c304, c162 + c610) : 0;
  const c119 = Math.min(remanente, c162);
  const c116 = remanente - c119;

  const c54 = sumar(filas(d, 'sueldos'), (f) => f.mayor_retencion);

  return {
    ...l1,
    1867: c1867, 155: c155,
    ...l9,
    1098: c1098, 161: c161,
    159: c159, 748: c748, 749: c749,
    169: c169, 158: c158, 170: c170, 157: c157, 1035: c1035,
    136: c136, 176: c176, 608: c608, 746: c746, 895: c895, 162: c162, 610: c610,
    304: c304, 31: c31, 119: c119, 116: c116, 54: c54,
  };
};

// Códigos que se guardan, en el orden del formulario
const CODIGOS_PERSONA = [
  '2005', '2006', '2007', '2008', '2009', '2000', '2001', '2002', '2003', '2004',
  '1592', '1024', '1593', '1025', '104',
  '1867', '155',
  '1879', '1880', '1881', '1882', '1883', '1633', '1105', '1634', '606', '152',
  '1098', '161', '159', '748', '749',
  '169', '158', '170', '157', '1035',
  '136', '176', '608', '746', '895', '162', '610',
  '304', '31', '119', '116', '54',
];

module.exports = { calcularPersona, igcSegunTabla, esPropio, CODIGOS_PERSONA };
